import { css } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";
import { CanvasComponent } from "./CanvasComponent";

/** CSS affecting the {@link WeldingMinigameComponent} */
const styles: string = css`
    :host {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        position: relative;
    }
    .container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }
    .message-box {
        top: 0;
        display: flex;
        flex-direction: row;
        align-items: center; /* This is crucial for vertical alignment */
        padding: 5px;
        border: none;
        text-align: center;
        gap: 10px; /* Better than margin for spacing between items */
    }

    .message-box .button {
        background-color: #856343;
        outline: 1px solid #5a3f25;
        border-top: 2px solid #c78d57;
        padding: 5px 10px;
        margin: 0; /* Remove vertical margin */
        text-transform: uppercase;
        cursor: var(--cursor-hover);
        display: inline-flex; /* Better for button contents */
        align-items: center; /* Center contents vertically */
        justify-content: center; /* Center contents horizontally */
        user-select: none;
        border-bottom: 2px solid #5a3f25;
        transition: all 0.1s ease-in-out;
        box-sizing: border-box; /* Changed from content-box */
        height: fit-content; /* Or set a fixed height */
        line-height: 1; /* Prevents extra vertical space */
    }

    .message-box .button:hover {
        filter: brightness(1.1);
        transform: translateY(-4px);
        border-bottom-width: 6px;
        margin-bottom: -4px; /* Compensate for transform */
    }

    .message-box .button:active,
    .message-box .button:focus {
        cursor: var(--cursor-active);
        filter: brightness(0.9);
        transform: translateY(0px);
        border-bottom-width: 2px;
        margin-bottom: 0; /* Reset hover margin */
    }
        img {
        width: 100%;
        height: auto;
        object-fit: contain;
    }
    canvas {
        cursor: var(--welding-default);
    }
    canvas:active {
        cursor: var(--welding-active)
    }
`;

/**
 * A custom element representing the welding minigame web component.
 * Players must "weld" by drawing on a canvas to fill in required pixels.
 */
export class WeldingMinigameComponent extends HTMLElement {
    private canvas!: HTMLCanvasElement;
    private reset!: HTMLButtonElement;
    private ctx!: CanvasRenderingContext2D;
    private isDrawing: boolean = false;
    private requiredPixels: Set<string> = new Set(); // Stores required pixel coordinates as strings (e.g., "x,y")
    private filledPixels: Set<string> = new Set(); // Stores filled pixel coordinates
    private backgrounds: string[] = ["/assets/img/rooms/beach_minigame/welding_level_1.png", ""]; // Background images for the minigame
    private currentBackground: number = 0; // Index of the current background
    private pixelFilePath: string[] = ["/assets/img/rooms/beach_minigame/welding_level_1_locations.txt", ""]; // Paths to files defining required pixels

    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /**
     * Called when the element is added to the DOM.
     * Initializes the shadow DOM and renders the component.
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.render();
    }

    /**
     * Renders the component's HTML and sets up the canvas and event listeners.
     * @private
     */
    private async render(): Promise<void> {
        if (!this.shadowRoot) return;

        // Render the component's HTML structure
        this.shadowRoot.innerHTML = `
        <style>${styles}</style>
        <div class="container">
            <div class="message-box">
                <p id='status'> Weld in between the lines!</p>
                <a class="button" id=resetBtn>Reset</a>
            </div>
            <canvas></canvas>
        </div>
        `;

        // Initialize canvas and reset button
        this.canvas = this.shadowRoot.querySelector("canvas") as HTMLCanvasElement;
        this.reset = this.shadowRoot.querySelector("#resetBtn") as HTMLButtonElement;
        this.canvas.width = 400;
        this.canvas.height = 300;

        // Load the background image
        const background: HTMLImageElement = new Image();
        background.src = this.backgrounds[this.currentBackground];
        this.ctx = this.canvas.getContext("2d")!;

        // Draw the background image once it's loaded
        background.onload = () => {
            this.ctx.drawImage(background, 0, 0, this.canvas.width, this.canvas.height);
        };

        // Define the required pixels and set up drawing functionality
        await this.defineRequiredPixels();
        this.drawing();
    }

    /**
     * Loads the required pixel coordinates from a file.
     * @private
     */
    private async defineRequiredPixels(): Promise<void> {
        try {
            const response: Response = await fetch(this.pixelFilePath[this.currentBackground]);
            const text: string = await response.text();
            const lines: string[] = text.split("\n");

            this.requiredPixels.clear();

            // Parse each line to extract pixel coordinates
            for (const line of lines) {
                const trimmed: string = line.trim();
                if (trimmed) {
                    const match: RegExpMatchArray | null = trimmed.match(/\((\d+),\s*(\d+)\)/);
                    if (match) {
                        const x: number = parseInt(match[1], 10);
                        const y: number = parseInt(match[2], 10);
                        this.requiredPixels.add(`${x},${y}`);
                    }
                }
            }

            // console.log(`Loaded ${this.requiredPixels.size} required pixels.`);
        }
        catch (error) {
            console.error("Error loading required pixels:", error);
        }
    }

    /**
     * Sets up event listeners for drawing on the canvas.
     * @private
     */
    private drawing(): void {
        // Reset the canvas when the reset button is clicked
        this.reset.addEventListener("click", () => this.render());

        // Start drawing when the mouse is pressed
        this.canvas.addEventListener("mousedown", () => this.isDrawing = true);

        // Stop drawing when the mouse is released
        this.canvas.addEventListener("mouseup", () => {
            this.isDrawing = false;
            this.ctx.beginPath();

            // Check if the welding is complete
            const complete: boolean = this.checkComplete();
            if (complete) {
                setTimeout(async () => {
                    await this.handleWeldingComplete();
                }, 1000);
            }
        });

        // Draw while the mouse is moving
        this.canvas.addEventListener("mousemove", event => this.draw(event));
    }

    /**
     * Draws on the canvas when the mouse is moved.
     * @param {MouseEvent} event - The mouse event containing the cursor's position.
     * @private
     */
    private draw(event: MouseEvent): void {
        if (!this.isDrawing) {
            return;
        }

        // Calculate the cursor's position relative to the canvas
        const rect: DOMRect = this.canvas.getBoundingClientRect();
        const x: number = Math.floor(event.clientX - rect.left);
        const y: number = Math.floor(event.clientY - rect.top);
        const brushSize: number = 10;

        // Fill pixels within the brush size
        for (let dx: number = -brushSize; dx <= brushSize; dx++) {
            for (let dy: number = -brushSize; dy <= brushSize; dy++) {
                const px: number = x + dx;
                const py: number = y + dy;
                const key: string = `${px},${py}`;

                if (this.requiredPixels.has(key)) {
                    this.filledPixels.add(key);
                }
            }
        }

        // Create a gradient for the welding effect
        const gradient: CanvasGradient = this.ctx.createRadialGradient(x, y, 1, x, y, 10);
        gradient.addColorStop(0, "rgba(255, 255, 200, 1)");
        gradient.addColorStop(0.5, "rgba(255, 165, 0, 0.8)");
        gradient.addColorStop(1, "rgba(255, 69, 0, 0)");

        // Draw the welding effect
        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        this.ctx.arc(x, y, 10, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.shadowBlur = 15;
        this.ctx.shadowColor = "orange";

        // Draw sparks for a more realistic effect
        for (let i: number = 0; i < 3; i++) {
            this.drawSpark(x, y);
        }

        this.ctx.shadowBlur = 0;
        this.ctx.shadowColor = "transparent";
    }

    /**
     * Draws a spark effect at the specified coordinates.
     * @param {number} x - The x-coordinate of the spark.
     * @param {number} y - The y-coordinate of the spark.
     * @private
     */
    private drawSpark(x: number, y: number): void {
        // console.log(`Drawing spark at ${x},${y}`); // Debug log
        const sparkSize: number = Math.random() * 4 + 1; // 1-5px size
        const sparkX: number = x + (Math.random() * 20 - 10); // Wider spread
        const sparkY: number = y + (Math.random() * 20 - 10);
        const opacity: number = Math.random() * 0.8 + 0.2; // 20-100% opacity
        const hue: number = 30 + Math.random() * 30; // Yellow-orange range

        this.ctx.fillStyle = `hsla(${hue}, 100%, 50%, ${opacity})`;
        this.ctx.beginPath();
        this.ctx.arc(sparkX, sparkY, sparkSize, 0, Math.PI * 2);
        this.ctx.fill();
    }

    /**
     * Checks if the welding is complete by comparing filled pixels to required pixels.
     * @returns {boolean} True if the welding is complete, otherwise false.
     * @private
     */
    private checkComplete(): boolean {
        const filled: number = [...this.requiredPixels].filter(px => this.filledPixels.has(px)).length;
        const percentage: number = (filled / this.requiredPixels.size) * 100;

        // Update the status message
        if (percentage >= 100) {
            (this.shadowRoot!.querySelector("#status") as HTMLParagraphElement).textContent = "Your welding is complete!";
            return true;
        }
        else {
            (this.shadowRoot!.querySelector("#status") as HTMLParagraphElement).textContent = `Progress: ${percentage.toFixed(1)}%`;
            return false;
        }
    }

    /**
     * Handles the completion of the welding minigame.
     * Notifies the game route service and refreshes the game state.
     * @private
     */
    private async handleWeldingComplete(): Promise<void> {
        await this._gameRouteService.weldingCompleted();
        const canvasComponent: CanvasComponent | null = document.querySelector("game-root");

        if (canvasComponent && canvasComponent.shadowRoot) {
            const gameCanvas: HTMLElement | null = canvasComponent.shadowRoot.querySelector("game-canvas");
            if (gameCanvas) {
                void (gameCanvas as CanvasComponent).refreshGameState();
            }
        }
    }
}
